import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Sparkles, FileText, Loader2, Calendar, Briefcase, Search } from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const ContactoClientes = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [clientesActivos, setClientesActivos] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedPSP, setSelectedPSP] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [fechaReunion, setFechaReunion] = useState('');
  const [transcription, setTranscription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchClientesActivos();
  }, []);

  const fetchClientesActivos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/contacto-clientes/clientes-activos');
      setClientesActivos(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes activos');
    } finally {
      setLoading(false);
    }
  };

  const getClientPSPs = () => {
    const client = clientesActivos.find(c => c.id === selectedClient);
    return client?.psps_activas || [];
  };

  // Filtrar clientes por búsqueda
  const filteredClientes = clientesActivos.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.company?.toLowerCase().includes(query)
    );
  });

  const handleProcessMeeting = async () => {
    if (!selectedClient || !selectedPSP || !meetingTitle || !transcription) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setProcessing(true);
    try {
      // Crear minuta
      const createRes = await api.post('/contacto-clientes/minuta', {
        client_id: selectedClient,
        title: meetingTitle,
        transcription: transcription,
        fecha_reunion: fechaReunion ? new Date(fechaReunion).toISOString() : null,
        proposal_id: selectedPSP,
      });

      const meetingId = createRes.data.meeting_id;

      // Procesar con IA
      const processRes = await api.post(`/contacto-clientes/${meetingId}/process`);

      if (processRes.data.derivadas_tablero_lideres) {
        toast.success(
          `Minuta procesada: ${processRes.data.tareas_creadas} tareas creadas y derivadas directamente al Tablero de Líderes`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `Minuta procesada: ${processRes.data.tareas_creadas} tareas creadas y asociadas a la PSP`,
          { duration: 5000 }
        );
      }

      // Limpiar formulario
      setMeetingTitle('');
      setFechaReunion('');
      setTranscription('');
      setSelectedPSP('');
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-4xl font-bold text-slate-900" data-testid="contacto-title">
                Contacto con Clientes
              </h1>
            </div>
            <p className="text-lg text-slate-600">
              Gestión de reuniones para clientes con servicios activos
            </p>
          </div>

          {/* Info Banner */}
          <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Briefcase className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-800 mb-1">Flujo Automático</h4>
                <p className="text-sm text-blue-700">
                  Las tareas extraídas de minutas para PSPs <strong>ya aceptadas</strong> se derivan 
                  directamente al <strong>Tablero de Líderes</strong> sin pasar por el Cerebro de Tareas.
                </p>
              </div>
            </div>
          </Card>

          {/* Main Form */}
          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-semibold text-slate-900">
                Nueva Minuta de Seguimiento
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                <p className="text-slate-600">Cargando clientes activos...</p>
              </div>
            ) : clientesActivos.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-lg text-slate-600 mb-2">No hay clientes con servicios activos</p>
                <p className="text-sm text-slate-500">
                  Los clientes aparecerán aquí cuando tengan una PSP en estado "Aceptada" o "En Ejecución"
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Barra de búsqueda de clientes */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar cliente por nombre o empresa..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="client-search"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cliente */}
                  <div>
                    <Label className="mb-2 block">Cliente Activo *</Label>
                    <Select value={selectedClient} onValueChange={(value) => {
                      setSelectedClient(value);
                      setSelectedPSP('');
                    }}>
                      <SelectTrigger data-testid="client-select">
                        <SelectValue placeholder="Seleccionar cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredClientes.length === 0 ? (
                          <div className="p-2 text-sm text-slate-500 text-center">
                            No se encontraron clientes
                          </div>
                        ) : (
                          filteredClientes.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name} {client.company ? `(${client.company})` : ''}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* PSP */}
                  <div>
                    <Label className="mb-2 block">PSP Asociada *</Label>
                    <Select 
                      value={selectedPSP} 
                      onValueChange={setSelectedPSP}
                      disabled={!selectedClient}
                    >
                      <SelectTrigger data-testid="psp-select">
                        <SelectValue placeholder={selectedClient ? "Seleccionar PSP" : "Primero seleccione cliente"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getClientPSPs().map((psp) => (
                          <SelectItem key={psp.id} value={psp.id}>
                            {psp.folio} - {psp.titulo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Título */}
                  <div>
                    <Label className="mb-2 block">Título de la Reunión *</Label>
                    <Input
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      placeholder="Ej: Seguimiento mensual - Marzo 2026"
                      data-testid="meeting-title-input"
                    />
                  </div>

                  {/* Fecha y Hora */}
                  <div>
                    <Label className="mb-2 block">Fecha y Hora de Reunión</Label>
                    <Input
                      type="datetime-local"
                      value={fechaReunion}
                      onChange={(e) => setFechaReunion(e.target.value)}
                      data-testid="fecha-reunion-input"
                    />
                  </div>
                </div>

                {/* Transcripción */}
                <div>
                  <Label className="mb-2 block">Transcripción de la Reunión *</Label>
                  <Textarea
                    value={transcription}
                    onChange={(e) => setTranscription(e.target.value)}
                    rows={10}
                    placeholder="Pega aquí la transcripción de Read.ai, Phantom u otra herramienta..."
                    className="font-mono text-sm"
                    data-testid="transcription-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleProcessMeeting}
                  disabled={processing || !selectedClient || !selectedPSP || !meetingTitle || !transcription}
                  className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
                  data-testid="process-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Procesando con IA...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Procesar Minuta
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactoClientes;
